import {
  LIST_MENTIONABLE_USERS,
  Media,
  Name,
  PermissionValues,
  useCurrentAccount,
  useCustomQuery,
  useFindByQuery,
} from '@gripp/shared-logic';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { AccountAvatar } from '../accountAvatar';
import { colors as ccColors } from '../crop-circle/tokens/colors';
import { FilterChip, filterChipStyleValues } from '../filter/filterChip';
import { AnonymousId, FunctionId, getIssueFullName } from './issueFormatUtils';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type ActivityMember = {
  id: string;
  name: string;
  avatar?: Media;
};

// Audit fields the sheet reads that the generated Issue type does not carry.
type IssueWithAudit = {
  id: string;
  createdBy?: { id?: string; name?: Name; avatar?: Media };
  workspace?: { id: string };
};

type MentionableUser = {
  id: string;
  name?: Name;
  email?: string;
  avatar?: Media;
};

type ActivityOptionsSheetProps = {
  issue: IssueWithAudit;
  mediaUrl?: string;
  onClose: () => void;
  /**
   * Admin-only add flow. Within a workspace this is notify-and-surface (a
   * mention message), not an access grant: workspace members already have
   * access to the activity. Swaps to Grant-backed membership in phase B.
   */
  onAddMember?: (member: ActivityMember) => void;
  /**
   * Opens the workspace invite flow (QR + share link) for people not yet in
   * the workspace or on Gripp at all. Requires account.invite permission.
   */
  onInvitePress?: () => void;
};

// Chat-options sheet dropping from the Activity top bar (kebab toggle) -
// mobile counterpart of the Gripp OS desktop chat-options band. Chat options
// are strictly the member roster: activity actions (edit, pin, delete) live
// on the activity settings page reached from the bar's center tap target.
//
// Members are derived from message authorship plus mentioned users (same
// authorship rule as sideChatPanel): there is no persisted participant model
// yet. Admins additionally get the ADD chips (manage); everyone else is
// read-only. Cross-organization invites are parked until the Grant ACL is
// implemented server-side; there is no Organization model today.
export const ActivityOptionsSheet = (props: ActivityOptionsSheetProps) => {
  const { t } = useTranslation();
  const { account } = useCurrentAccount();
  // Members added this session, surfaced immediately; the mention message
  // makes them part of the derived roster on the next load.
  const [sessionAdded, setSessionAdded] = useState<ActivityMember[]>([]);

  const issueId = props.issue?.id;
  const workspaceId = props.issue?.workspace?.id;

  // Manage = workspace admin or global admin (roles.json grants these
  // wildcards literally). Everyone else gets the read-only roster.
  const canManageMembers =
    !!props.onAddMember &&
    (account?.effectivePermissions?.includes('*.*') ||
      account?.effectivePermissions?.includes('*.*.workspace'));

  // Invite pill: gated like the plus-menu Invite tab (account.invite), on top
  // of the admin manage gate.
  const canInvite =
    !!props.onInvitePress &&
    !!account?.hasPermission?.(PermissionValues.account.invite);

  const filter = useMemo(() => ({ issue: { eq: issueId } }), [issueId]);

  // Lean authors-only page; the sheet mounts on demand so this only runs when
  // opened. 100 messages of lookback matches what "who is in this chat" means
  // in practice until a real participant model exists.
  const { data: messagesData } = useFindByQuery({
    modelName: 'issueMessage',
    query: FIND_MESSAGE_AUTHORS,
    variables: { filter, page: { limit: 100 } },
  });

  const members = useMemo<ActivityMember[]>(() => {
    const byId = new Map<string, ActivityMember>();
    const add = (audit?: { id?: string; name?: Name; avatar?: Media }) => {
      const id = audit?.id;
      if (!id || id === AnonymousId || id === FunctionId) return;
      if (!byId.has(id)) {
        byId.set(id, {
          id,
          name: getIssueFullName(id, audit?.name ?? undefined),
          avatar: audit?.avatar,
        });
      }
    };
    add(props.issue?.createdBy);
    (messagesData?.items ?? []).forEach(
      (m: {
        updatedBy?: { id?: string; name?: Name; avatar?: Media };
        userMentions?: { id?: string; name?: Name; avatar?: Media }[];
      }) => {
        add(m.updatedBy);
        m.userMentions?.forEach(add);
      }
    );
    sessionAdded.forEach((m) => {
      if (!byId.has(m.id)) byId.set(m.id, m);
    });
    // Current account first, rest in first-seen order.
    const all = [...byId.values()];
    const selfId = account?.id;
    return all.sort((a, b) => (a.id === selfId ? -1 : b.id === selfId ? 1 : 0));
  }, [messagesData, props.issue, account?.id, sessionAdded]);

  // Workspace-scoped people picker, admins only. Same source as the @ mention
  // field. Nested under `account { listMentionableUsers }`, so useCustomQuery
  // with an explicit path (factory unwrap does not reach nested queries).
  const { data: mentionableData } = useCustomQuery({
    query: LIST_MENTIONABLE_USERS,
    cacheKey: ['activityMembersAddable', workspaceId],
    variables: {
      filter: { workspaceId },
      page: { limit: 20 },
    },
    options: {
      enabled: !!workspaceId && canManageMembers,
    },
  });

  const addableUsers = useMemo<ActivityMember[]>(() => {
    const items: MentionableUser[] =
      (
        mentionableData as {
          account?: { listMentionableUsers?: { items?: MentionableUser[] } };
        }
      )?.account?.listMentionableUsers?.items ?? [];
    const memberIds = new Set(members.map((m) => m.id));
    return items
      .filter((u) => u.id !== account?.id && !memberIds.has(u.id))
      .map((u) => ({
        id: u.id,
        name: getIssueFullName(u.id, u.name ?? undefined),
        avatar: u.avatar,
      }));
  }, [mentionableData, members, account?.id]);

  const handleAdd = (member: ActivityMember) => {
    setSessionAdded((prev) =>
      prev.some((m) => m.id === member.id) ? prev : [...prev, member]
    );
    props.onAddMember?.(member);
  };

  return (
    <>
      {/* Backdrop over the messages; tap closes. Oversized height so it
          covers the scroll area below the header's stacking context. */}
      <Pressable
        style={styles.backdrop}
        onPress={props.onClose}
        accessibilityLabel={t('topNav.customAlert.exit')}
        testID="activity-options-backdrop"
      />
      <View style={styles.sheet} testID="activity-options-sheet">
        <Text style={styles.sectionHeader}>
          {t('issue.members.title').toUpperCase()}
        </Text>
        <View style={styles.membersList}>
          {members.map((member) => (
            <View key={member.id} style={styles.memberRow}>
              <AccountAvatar
                avatar={member.avatar}
                mediaUrl={props.mediaUrl ?? ''}
                size={28}
              />
              <Text style={styles.memberName} numberOfLines={1}>
                {member.name}
              </Text>
            </View>
          ))}
        </View>

        {canManageMembers && (addableUsers.length > 0 || canInvite) && (
          <>
            <Text style={[styles.sectionHeader, styles.addHeader]}>
              {t('issue.members.add').toUpperCase()}
            </Text>
            <View style={styles.chipsRow}>
              {addableUsers.map((user) => (
                <FilterChip
                  key={user.id}
                  label={user.name}
                  selected={false}
                  onPress={() => handleAdd(user)}
                  leading={
                    <AccountAvatar
                      avatar={user.avatar}
                      mediaUrl={props.mediaUrl ?? ''}
                      size={avatarSize}
                    />
                  }
                  trailing={<Text style={styles.chipPlus}>+</Text>}
                  accessibilityLabel={t('issue.members.addPerson', {
                    name: user.name,
                  })}
                  testID={`activity-members-add-${user.id}`}
                />
              ))}
              {canInvite && (
                <TouchableOpacity
                  style={styles.inviteChip}
                  onPress={props.onInvitePress}
                  activeOpacity={0.7}
                  accessibilityRole="button"
                  accessibilityLabel={t('plusMenu.invite')}
                  testID="activity-members-invite"
                >
                  <Text style={styles.inviteChipLabel}>
                    {t('plusMenu.invite')}
                  </Text>
                  <Text style={styles.inviteChipPlus}>+</Text>
                </TouchableOpacity>
              )}
            </View>
          </>
        )}
      </View>
    </>
  );
};

/** Sized to the taller filter-chip metrics: 20pt leaves an even 10pt above and
 *  below inside the chip's 40pt height. */
const avatarSize = 20;

const styles = themedStyleSheet((role) => ({
  backdrop: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    height: 2000,
    backgroundColor: 'rgba(2, 2, 2, 0.3)',
  },
  sheet: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    backgroundColor: role.surface,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: role.borderControl,
  },
  sectionHeader: {
    fontSize: 11,
    lineHeight: 16,
    fontWeight: '600',
    letterSpacing: 0.5,
    color: role.textSecondary,
  },
  addHeader: {
    marginTop: 12,
  },
  membersList: {
    marginTop: 8,
  },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 6,
  },
  memberName: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
    color: role.textStrong,
  },
  chipsRow: {
    ...filterChipStyleValues.optionsContainer,
    marginTop: 6,
  },
  chipPlus: {
    ...filterChipStyleValues.chipText,
    color: role.textSecondary,
  },
  // Distinct fill: this chip leaves the workspace roster and opens the invite
  // flow (QR + link) for people not in the workspace or on Gripp. Built from
  // the canonical chip metrics (rather than rendered as a FilterChip) because
  // its orange fill is not one of the chip's selected/unselected states, but
  // it has to sit on the same baseline and height as its neighbours.
  inviteChip: {
    ...filterChipStyleValues.chip,
    backgroundColor: ccColors.orange[500],
  },
  inviteChipLabel: {
    ...filterChipStyleValues.chipText,
    color: role.textOnBrand,
  },
  inviteChipPlus: {
    ...filterChipStyleValues.chipText,
    color: role.textOnBrand,
  },
}));

const FIND_MESSAGE_AUTHORS = `
  query findIssueMessages($filter: IssueMessageFilter, $page: PageRequest) {
    findIssueMessages(filter: $filter, page: $page) {
      items {
        id
        updatedBy {
          id
          name {
            first
            last
          }
          avatar {
            id
            extension
          }
        }
        userMentions {
          id
          name {
            first
            last
          }
          avatar {
            id
            extension
          }
        }
      }
      count
      offset
      limit
    }
  }
`;
